import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'antd';
import { isRussia } from 'utils/countryCode';
import { isOsrSummarySupportedCrop } from 'pages/RecommendationV2/utils/isOsrSummarySupportedCrop';
import {
  RecommendationInfoSection,
  RecommendationInfoWrapper,
  RecommendationInfoTitle,
  RecommendationInfoUserName,
  RecommendationInfoDate,
  RecommendationInfoFieldInfo,
  RecommendationInfoReportOverview,
  RecommendationInfoFieldInfoMobilePrimary,
  RecommendationInfoFieldInfoMobileSecondary,
  RecommendationInfoFieldInfoContainer,
  RecommendationInfoTitleNonDDG,
  RecommendationInfoUserDetailsNonDDG,
} from './RecommendationInfo.styles';
import { useAppState } from 'context/AppState';
import Calendar from 'assets/icons/calendar_today.svg';
import TopBarPattern from 'assets/icons/topbar_pattern.svg';
import Separator from 'assets/icons/divider_vertical.svg';
import Phone from 'assets/icons/local_phone.svg';
import Mail from 'assets/icons/mail.svg';
import { useBreakpoint } from 'hooks';
import { getCropName, useSkeletonLoader } from '../HeroBanner/heroBannerUtils';
import FieldDetails from '../HeroBanner/FieldDetails';

import ReportOverviewContent from '../ReportOverview/ReportOverviewContent';

interface RecInfoProps {
  isRecommendationAPILoading: boolean;
  isNonDDG: boolean;
}

const RecommendationInfo = (props: RecInfoProps): JSX.Element => {
  const { isRecommendationAPILoading, isNonDDG } = props;
  const { isMobile } = useBreakpoint();
  const [t, i18n] = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const {
    apiData: { recommendation, recommendationCropwise, nonDDGUserDetails },
  } = useAppState();
  const fieldSummaryArea = recommendationCropwise ? recommendationCropwise.summary_area : '';
  const date = recommendationCropwise && new Date(recommendationCropwise.created_on);
  const recommendationCW = recommendationCropwise?.recommendations[0];
  const userName = recommendation?.userContactFromAudit;
  const isDDG = recommendationCropwise?.source === 'DDG';

  const handleCollapseChange = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
  };

  const generatedBy = recommendationCW?.multi_field?.config?.agronomicInputs?.generatedby;
  const isScriptGenerated = generatedBy === 'script';
  const formattedDate = date?.toLocaleString(i18n.language, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const SkeletonLoader = useSkeletonLoader();
  const cropName = getCropName(recommendationCW, recommendationCropwise, t);
  const isAISummarySupported =
    isOsrSummarySupportedCrop(recommendationCropwise?.recommendations[0]?.crop_name) &&
    !isRussia(recommendationCropwise?.country) &&
    !isDDG;

  const getNonDDGUserDetails = () => {
    return (
      <>
        <RecommendationInfoTitleNonDDG>
          {recommendationCropwise?.organization_name}
        </RecommendationInfoTitleNonDDG>
        <RecommendationInfoDate>
          <img src={Calendar} alt="No img found" style={{ marginRight: '8px' }} />
          {`${t('Created')}: `}&nbsp;<b>{formattedDate}</b>
        </RecommendationInfoDate>
        {!isScriptGenerated && (
          <RecommendationInfoUserDetailsNonDDG
            isMobile={isMobile}
            data-testid="non-ddg-user-contact-details"
          >
            <div style={{ textAlign: 'left' }}>
              {nonDDGUserDetails?.first_name ? (
                <>
                  {`${t('By')}: ${nonDDGUserDetails?.first_name || ''} ${
                    nonDDGUserDetails?.last_name || ''
                  }`}
                  {!isMobile && (
                    <img src={Separator} alt="No img found" className="separator-icon" />
                  )}
                </>
              ) : (
                <Skeleton.Button active size={'default'} shape={'default'} block={false} />
              )}
            </div>
            <div className={isMobile ? 'user-details-mobile' : 'user-details-desktop'}>
              {!!nonDDGUserDetails?.phone && (
                <div className={isMobile ? 'contact-details-mobile' : 'contact-details-desktop'}>
                  <img src={Phone} alt="No img found" className="icon-padding" />
                  <div className={isMobile ? 'user-details-text' : ''}>
                    {nonDDGUserDetails?.email ? (
                      <a className="text-link" href={`tel:+${nonDDGUserDetails?.phone}`}>
                        {nonDDGUserDetails?.phone}
                      </a>
                    ) : (
                      <Skeleton.Button active size={'default'} shape={'default'} block={false} />
                    )}
                  </div>
                  <img src={Separator} alt="No img found" className="separator-icon" />
                </div>
              )}
              <div className={isMobile ? 'contact-details-mobile' : 'contact-details-desktop'}>
                <img src={Mail} alt="No img found" className="icon-padding" />
                <div className={isMobile ? 'user-details-text' : ''}>
                  {nonDDGUserDetails?.email ? (
                    <a className="text-link" href={`mailto:${nonDDGUserDetails?.email}`}>
                      {nonDDGUserDetails?.email}
                    </a>
                  ) : (
                    <Skeleton.Button active size={'default'} shape={'default'} block={false} />
                  )}
                </div>
              </div>
            </div>
          </RecommendationInfoUserDetailsNonDDG>
        )}
      </>
    );
  };

  const getDDGUserDetails = () => {
    return (
      <>
        <RecommendationInfoTitle>{`${t('CREATED FOR')}:`}</RecommendationInfoTitle>
        <RecommendationInfoUserName data-testid="user-name">
          {recommendation && `${userName?.firstName} ${userName?.lastName}`}
        </RecommendationInfoUserName>
        <RecommendationInfoDate>
          <img src={Calendar} alt="No img found" style={{ marginRight: '8px' }} />
          {`${t('Created')}: `}&nbsp;<b>{formattedDate}</b>
        </RecommendationInfoDate>
      </>
    );
  };

  const totalFields = recommendationCW?.multi_field?.assignments?.length ?? 0;
  const loading = isRecommendationAPILoading;

  return (
    <RecommendationInfoWrapper cropName={recommendationCW?.crop_name?.toLowerCase() ?? ''}>
      {loading ? (
        SkeletonLoader
      ) : (
        <RecommendationInfoSection isMobile={isMobile}>
          <div className="recommendation-info-first-section">
            {isNonDDG ? getNonDDGUserDetails() : getDDGUserDetails()}
          </div>
          {!isMobile && <img src={TopBarPattern} alt="No img found" className="leaflet-image" />}
          {isMobile ? (
            <FieldDetails
              totalFields={totalFields}
              fieldSummaryArea={fieldSummaryArea}
              cropNameLowerCase={recommendationCW?.crop_name?.toLowerCase() ?? ''}
              cropName={cropName}
              t={t}
              isMobile={true}
              showFieldInfo={!isScriptGenerated}
              renderMobilePrimary={(content) => (
                <RecommendationInfoFieldInfoContainer>
                  <RecommendationInfoFieldInfoMobilePrimary data-testid="rec-field-infomobile">
                    {content}
                  </RecommendationInfoFieldInfoMobilePrimary>
                </RecommendationInfoFieldInfoContainer>
              )}
              renderMobileSecondary={(content) => (
                <RecommendationInfoFieldInfoMobileSecondary>
                  {content}
                </RecommendationInfoFieldInfoMobileSecondary>
              )}
            />
          ) : (
            <FieldDetails
              totalFields={totalFields}
              fieldSummaryArea={fieldSummaryArea}
              cropNameLowerCase={recommendationCW?.crop_name?.toLowerCase() ?? ''}
              cropName={cropName}
              t={t}
              showFieldInfo={!isScriptGenerated}
              renderDesktop={(content) => (
                <RecommendationInfoFieldInfo data-testid="rec-field-info">
                  {content}
                </RecommendationInfoFieldInfo>
              )}
            />
          )}
          <RecommendationInfoReportOverview
            isAISummarySupported={isAISummarySupported}
            isCollapsed={isCollapsed}
          >
            <ReportOverviewContent
              isCollapsed={isCollapsed}
              onCollapseChange={handleCollapseChange}
              isAISummarySupported={isAISummarySupported}
            />
          </RecommendationInfoReportOverview>
        </RecommendationInfoSection>
      )}
    </RecommendationInfoWrapper>
  );
};

export default RecommendationInfo;
